/**
 * Dark mode color adapter (stylis plugin).
 *
 * Many TMS pages hard-code light-mode colors in `sx` / `styled` (e.g. `bgcolor: "#fff"`,
 * `color: "#0f172a"`, `border: "1px solid #e2e8f0"`). Instead of patching every page,
 * this plugin runs only inside the dark-mode Emotion cache and remaps those values to the
 * dark palette at style-generation time:
 *
 *  - light backgrounds  -> layered dark surfaces (lighter light-mode tone = more elevated)
 *  - pastel/tinted fills -> deep tints of the same hue (status chips, badges, alerts)
 *  - dark text           -> readable light text (neutral) or brightened hue (colored)
 *  - light borders       -> subtle dark borders
 *  - faint black overlays (hover/dividers) -> faint white overlays
 *
 * Colors that already work on a dark background (saturated buttons, white text, dark
 * surfaces) are left untouched, so the adapter is safe for components that already
 * support dark mode. Light mode never goes through this plugin.
 */

import { DECLARATION } from "stylis";

/**
 * Opt-out for colors that must stay as written in dark mode (e.g. a white switch thumb).
 * Values containing var() are skipped by the adapter; the undefined custom property
 * simply falls back to the given color.
 */
export const keepColor = (color) => `var(--tms-keep, ${color})`;

// Navy hue/saturation used for neutral surfaces so they match the dark palette.
const SURFACE_HUE = 220;

const NAMED_COLORS = {
  white: [255, 255, 255],
  black: [0, 0, 0],
  whitesmoke: [245, 245, 245],
  ghostwhite: [248, 248, 255],
  snow: [255, 250, 250],
  aliceblue: [240, 248, 255],
  azure: [240, 255, 255],
  ivory: [255, 255, 240],
  gainsboro: [220, 220, 220],
  lightgray: [211, 211, 211],
  lightgrey: [211, 211, 211],
  silver: [192, 192, 192],
  lavender: [230, 230, 250],
  beige: [245, 245, 220],
  linen: [250, 240, 230],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  dimgray: [105, 105, 105],
  dimgrey: [105, 105, 105],
  blue: [0, 0, 255],
  navy: [0, 0, 128],
  darkblue: [0, 0, 139],
  green: [0, 128, 0],
  darkgreen: [0, 100, 0],
  red: [255, 0, 0],
  darkred: [139, 0, 0],
  maroon: [128, 0, 0],
  purple: [128, 0, 128],
  indigo: [75, 0, 130],
};

const COLOR_RE = new RegExp(
  [
    "#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3,4})\\b",
    "rgba?\\([^)]*\\)",
    "hsla?\\([^)]*\\)",
    `\\b(?:${Object.keys(NAMED_COLORS).join("|")})\\b`,
  ].join("|"),
  "gi"
);

const BG_PROPS = new Set(["background", "background-color", "background-image"]);
const TEXT_PROPS = new Set(["color", "-webkit-text-fill-color"]);
const isBorderProp = (prop) =>
  prop.startsWith("border") || prop.startsWith("outline") || prop === "box-shadow";

const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));

export function parseColor(str) {
  const s = str.trim().toLowerCase();

  if (NAMED_COLORS[s]) return [...NAMED_COLORS[s], 1];

  if (s[0] === "#") {
    let hex = s.slice(1);
    if (hex.length <= 4) hex = hex.split("").map((c) => c + c).join("");
    const n = (i) => parseInt(hex.slice(i, i + 2), 16);
    return [n(0), n(2), n(4), hex.length === 8 ? n(6) / 255 : 1];
  }

  const inner = s.slice(s.indexOf("(") + 1, s.lastIndexOf(")"));
  if (inner.includes("var(")) return null;
  const parts = inner.split(/[\s,/]+/).filter(Boolean);
  if (parts.length < 3) return null;

  const num = (p, scale) => (p.endsWith("%") ? (parseFloat(p) / 100) * scale : parseFloat(p));
  const alpha = parts[3] !== undefined ? num(parts[3], 1) : 1;

  if (s.startsWith("rgb")) {
    const rgb = parts.slice(0, 3).map((p) => num(p, 255));
    if (rgb.some(Number.isNaN)) return null;
    return [...rgb, alpha];
  }

  const h = parseFloat(parts[0]);
  const sat = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;
  if ([h, sat, l].some(Number.isNaN)) return null;
  return [...hslToRgb(h, sat, l), alpha];
}

function rgbToHsl(r, g, b) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l, c: 0 };

  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;

  return { h: (h * 60 + 360) % 360, s, l, c: d };
}

function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rgb;
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return rgb.map((v) => Math.round((v + m) * 255));
}

const toCss = (h, s, l, a) => {
  const [r, g, b] = hslToRgb(h, clamp(s), clamp(l));
  const alpha = Math.round(a * 1000) / 1000;
  return alpha >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isNeutral = ({ c }) => c < 0.06;
const isFaintBlack = ({ l }, a) => l < 0.15 && a <= 0.3;

// Lightness above which a fill is treated as a light-mode surface (tinted fills such as
// pastel/disabled states start a little lower than neutral greys).
const LIGHT_NEUTRAL_L = 0.75;
const LIGHT_TINT_L = 0.72;

/** Light surface -> dark surface. Returns null when the color should be kept. */
export function mapBackground(color) {
  const [r, g, b, a] = color;
  const hsl = rgbToHsl(r, g, b);

  if (isFaintBlack(hsl, a)) return `rgba(255, 255, 255, ${Math.round(a * 0.9 * 1000) / 1000})`;
  if (hsl.l < (isNeutral(hsl) ? LIGHT_NEUTRAL_L : LIGHT_TINT_L)) return null;

  if (isNeutral(hsl)) {
    const l = 0.13 + (1 - hsl.l) * 0.6;
    return toCss(SURFACE_HUE, 0.4 - (l - 0.13) * 0.6, l, a);
  }

  return toCss(hsl.h, Math.min(hsl.s, 0.7) * 0.6 + 0.1, 0.16 + (1 - hsl.l) * 0.4, a);
}

function relativeLuminance(r, g, b) {
  const channel = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Dark text -> light text. */
export function mapText(color) {
  const [r, g, b, a] = color;
  const hsl = rgbToHsl(r, g, b);

  if (hsl.c < 0.12) {
    if (hsl.l >= 0.6) return null;
    return toCss(hsl.s > 0 ? hsl.h : SURFACE_HUE, Math.min(hsl.s, 0.25), 0.96 - hsl.l * 0.62, a);
  }

  // Colored text: only brighten when it lacks contrast on dark surfaces (~4.5:1).
  if (relativeLuminance(r, g, b) >= 0.2) return null;
  const l = hsl.l < 0.5 ? 0.7 - hsl.l * 0.1 : Math.max(hsl.l, 0.7);
  return toCss(hsl.h, Math.min(hsl.s, 0.85), l, a);
}

/** Light border/ring -> subtle dark border. */
export function mapBorder(color) {
  const [r, g, b, a] = color;
  const hsl = rgbToHsl(r, g, b);

  if (isFaintBlack(hsl, a)) return `rgba(255, 255, 255, ${Math.round(a * 1000) / 1000})`;
  if (hsl.l < 0.75) return null;

  if (isNeutral(hsl)) return toCss(SURFACE_HUE, 0.25, 0.2 + (1 - hsl.l) * 0.4, a);
  return toCss(hsl.h, Math.min(hsl.s, 0.7) * 0.5, 0.3, a);
}

function replaceColors(value, mapper) {
  // Keep url(...) segments intact (image names may contain color words).
  return value
    .split(/(url\([^)]*\))/i)
    .map((segment) =>
      segment.toLowerCase().startsWith("url(")
        ? segment
        : segment.replace(COLOR_RE, (match) => {
            const parsed = parseColor(match);
            return (parsed && mapper(parsed)) || match;
          })
    )
    .join("");
}

/**
 * A rule painted with a bright, saturated fill (e.g. yellow badge with dark text) must keep
 * its dark text, otherwise contrast would be lost.
 */
function isBrightSaturated(value) {
  const matches = String(value).match(COLOR_RE) || [];
  return matches.some((match) => {
    const parsed = parseColor(match);
    if (!parsed || parsed[3] < 0.5) return false;
    const hsl = rgbToHsl(parsed[0], parsed[1], parsed[2]);
    return hsl.c >= 0.12 && hsl.l >= 0.5 && hsl.l < LIGHT_TINT_L;
  });
}

function hasBrightSaturatedFill(rule) {
  if (!rule || !Array.isArray(rule.children)) return false;

  let fills = rule.children.filter(
    (decl) => decl.type === DECLARATION && BG_PROPS.has(String(decl.props).toLowerCase())
  );
  // Follow the cascade: !important fills win, and a `background` shorthand resets earlier ones.
  const important = fills.filter((decl) => String(decl.children).includes("!important"));
  if (important.length) fills = important;

  return fills.reduce((bright, decl) => {
    const isShorthand = String(decl.props).toLowerCase() === "background";
    return isShorthand ? isBrightSaturated(decl.children) : bright || isBrightSaturated(decl.children);
  }, false);
}

export default function darkColorAdapter(element) {
  if (element.type !== DECLARATION || element.return) return;

  const prop = String(element.props).toLowerCase();
  let mapper = null;

  if (BG_PROPS.has(prop)) mapper = mapBackground;
  else if (TEXT_PROPS.has(prop)) mapper = hasBrightSaturatedFill(element.parent) ? null : mapText;
  else if (isBorderProp(prop)) mapper = mapBorder;

  if (!mapper) return;

  const value = String(element.children);
  if (!value || value.includes("var(")) return;

  const next = replaceColors(value, mapper);
  if (next === value) return;

  element.children = next;
  element.value = `${element.props}:${next};`;
}
